import { omit, some, toLower, values } from 'lodash-es';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Link from '@mui/material/Link';

import { graphql } from '__generated__';

import { AuthActions } from 'common/auth/actions';
import type { UserSecurityQuestions } from 'common/auth/types';
import { Form } from 'common/component/ValidationFormAndField';
import { nonAuthClient } from 'common/graphql/client';
import { required } from 'common/utils/validations';
import { CISecondaryButton } from 'common/viewcomp/forms/buttons';
import { CITextField } from 'common/viewcomp/forms/fields/text';
import 'module/auth/security_question/security_question.scss';

export interface SecurityQuestionFormProps {
    noQuestionsOnFileAction: 'showUnknownUsernameError' | 'goToActivation';
    onQuestionsFound: () => void;
    onNeedsActivation: () => void;
    onForgotUsername: () => void;
    onBackToLogin: () => void;
    className: string;
}

interface State {
    isLoading: boolean;
    hasSubmitted: boolean;
    username: string;
}

export const SecurityQuestionForm = ({
    noQuestionsOnFileAction,
    onQuestionsFound,
    onNeedsActivation,
    onForgotUsername,
    onBackToLogin,
    className,
}: SecurityQuestionFormProps): ReactElement => {
    const { t } = useTranslation('auth');
    const dispatch = useDispatch();
    const [state, setState] = useState<State>({
        isLoading: false,
        hasSubmitted: false,
        username: '',
    });
    const [usernameErrorState, setUsernameErrorState] = useState({
        hasUsernameError: false,
    });

    function fetchSecurityQuestions(username: string): void {
        nonAuthClient
            .query({
                query: GET_QUESTIONS,
                variables: {
                    username,
                },
            })
            .then((response) => {
                const securityQuestions = response.data.getUserSecurityQuestions;
                const allQuestionsOnFile = !some(values(securityQuestions), (value) => value === null);
                if (allQuestionsOnFile) {
                    dispatch(
                        AuthActions.forgotCredentialsQuestionsFound(
                            username,
                            omit(securityQuestions, '__typename') as UserSecurityQuestions
                        )
                    );
                    setState({
                        ...state,
                        isLoading: false,
                        hasSubmitted: false,
                    });
                    onQuestionsFound();
                } else {
                    setState({
                        ...state,
                        isLoading: false,
                        hasSubmitted: false,
                    });
                    if (noQuestionsOnFileAction === 'goToActivation') {
                        dispatch(AuthActions.forgotCredentialsActivationNeeded(username));
                        onNeedsActivation();
                    } else {
                        setUsernameErrorState({
                            hasUsernameError: true,
                        });
                    }
                }
            })
            .catch(() => {
                setState({
                    ...state,
                    isLoading: false,
                    hasSubmitted: false,
                });
                setUsernameErrorState({
                    hasUsernameError: true,
                });
            });
    }

    function securityQuestionSubmit(form: { username: string }): void {
        setState({
            ...state,
            isLoading: true,
            hasSubmitted: true,
            username: form.username,
        });
    }

    const { isLoading, hasSubmitted, username } = state;
    useEffect(() => {
        hasSubmitted && username && fetchSecurityQuestions(toLower(username));
    }, [hasSubmitted]);

    return (
        <div className={`security_question_container ${className}`}>
            <h1 className="title" id="forgot_password_enter_username_header">
                {t('security_question.primary_title')}
            </h1>
            <Form
                onSubmit={securityQuestionSubmit}
                render={({ handleSubmit, invalid }): ReactNode => {
                    const disableButton = invalid || usernameErrorState.hasUsernameError;

                    return (
                        <form onSubmit={handleSubmit} noValidate>
                            <CITextField
                                t={t}
                                className={usernameErrorState.hasUsernameError ? 'nameErr' : ''}
                                id="username"
                                name="username"
                                labelI18NKey="security_question.username_label"
                                type="text"
                                margin="normal"
                                fullWidth
                                required
                                helperText={
                                    usernameErrorState.hasUsernameError ? t('security_question.user_name_error') : ''
                                }
                                validations={[required()]}
                            />
                            <CISecondaryButton
                                className={`btn submit_button ${isLoading && 'btn_disabler'}`}
                                type="submit"
                                fullWidth
                                id="security_question_submit_button"
                                disabled={disableButton}
                                isLoading={isLoading}
                            >
                                {t('security_question.submit')}
                            </CISecondaryButton>
                            <CISecondaryButton
                                className="btn"
                                type="button"
                                fullWidth
                                id="security_fpswd_button"
                                disabled={false}
                                onClick={onForgotUsername}
                            >
                                {t('security_question.forgot_username')}
                            </CISecondaryButton>
                            <FormSpy
                                subscription={{
                                    error: true,
                                    valid: true,
                                    values: true,
                                    initialValues: true,
                                    pristine: true,
                                }}
                                onChange={(): void => {
                                    setUsernameErrorState({
                                        hasUsernameError: false,
                                    });
                                }}
                            />
                        </form>
                    );
                }}
            />
            <div className="link_container">
                <Link component="button" variant="body2" className="auth-link" id="backToLogin" onClick={onBackToLogin}>
                    {t('security_question.back_to_login')}
                </Link>
            </div>
        </div>
    );
};

const GET_QUESTIONS = graphql(`
    query GetQuestions($username: String!) {
        getUserSecurityQuestions(username: $username) {
            securityQuestion1Id
            securityQuestion1Text
            securityQuestion2Id
            securityQuestion2Text
            securityQuestion3Id
            securityQuestion3Text
        }
    }
`);
